import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useJob } from "../services";

export const EmployeeJobViewContext = createContext<any>(null);

export function EmployeeJobViewProvider({ children }) {
  const { id } = useParams();
  const { getJobDetailsById, updateTaskWorkingStatus, submitAssignedTask } = useJob();

  const [jobData, setJobData] = useState<Record<string, any>>({}); // typed as any for dynamic API response
  const [loadStatus, setLoadStatus] = useState({ loading: false, error: null });
  const [timeLeft, setTimeLeft] = useState("");

  const commonJobDetails = useMemo(
    () => ({
      jobCode: jobData.job_code,
      jobName: jobData.job_name,
      jobStatus: jobData.job_status,
    }),
    [jobData.job_code, jobData.job_name, jobData.job_status]
  );

  const jobCode = useMemo(() => jobData.job_code || "", [jobData.job_code]);
  const jobName = useMemo(() => jobData.job_name || "", [jobData.job_name]);
  const hasWorkAssigned = useMemo(() => Array.isArray(jobData.tasks) && jobData.tasks.length > 0, [jobData.tasks]);

  const typeOfWorkObj = useMemo(() => jobData.type_of_work || {}, [jobData]);
  const jobType = useMemo(() => typeOfWorkObj.job_type, [typeOfWorkObj]);
  const jobWorkType = useMemo(() => typeOfWorkObj.job_work_type, [typeOfWorkObj]);
  const isNewWork = useMemo(() => jobType === "New Job", [jobType]);
  const isExistingWork = useMemo(() => jobType === "Existing Job", [jobType]);
  const isAlliedWork = useMemo(() => jobWorkType === "Allied Work", [jobWorkType]);
  const isBuildingWork = useMemo(() => jobWorkType === "Building Work", [jobWorkType]);

  const columnDrawingString = useMemo(() => {
    if (!isAlliedWork && !isNewWork) return null;
    const count = typeOfWorkObj?.column_drawings_count ?? 2;
    return `C1 - C${count}`;
  }, [isAlliedWork, isNewWork, typeOfWorkObj?.column_drawings_count]);

  const slabsCountString = useMemo(() => {
    const {
      basement_count = 0,
      parking_count = 0,
      ground_floor = "None",
      upper_floor_count = 0,
      existing_floor_count = 0,
      structural_drawings_count,
      column_drawings_count = 2,
    } = typeOfWorkObj || {};

    // Calculate ground floor drawings
    const groundFloorDrawings = ground_floor === "None" ? 0 : ground_floor === "Ground Level" ? 1 : 2;

    // Calculate total structural drawings
    const totalStructuralDrawings =
      structural_drawings_count ??
      (isAlliedWork
        ? 2
        : 2 + basement_count + parking_count + groundFloorDrawings + upper_floor_count + existing_floor_count);

    const slabsCount = totalStructuralDrawings - column_drawings_count + 2;

    if (slabsCount <= 0) return "No slabs";

    if (isNewWork || isAlliedWork) {
      return slabsCount === 1 ? "S1" : `S1 - S${slabsCount}`;
    }

    // For existing work
    const totalDrawings =
      2 + basement_count + parking_count + groundFloorDrawings + upper_floor_count + existing_floor_count;
    const newDrawings = upper_floor_count - existing_floor_count + 2;
    const start = totalDrawings - newDrawings + 1;

    return slabsCount === 1 ? `S${start}` : `S${start} - S${totalDrawings}`;
  }, [isAlliedWork, isNewWork, typeOfWorkObj]);

  const typeOfWork = useMemo(() => {
    const baseData = {
      jobType,
      isAlliedWork,
      isBuildingWork,
      columnDrawings: columnDrawingString,
      slabsCount: slabsCountString,
    };

    if (isAlliedWork) {
      return {
        ...baseData,
        alliedWorkSpecification: typeOfWorkObj.allied_work_details || "",
      };
    }

    const buildingData = {
      ...baseData,
      buildingType: typeOfWorkObj.building_type || "",
      basementCount: typeOfWorkObj.basement_count || 0,
      parkingCount: typeOfWorkObj.parking_count || 0,
      groundFloor: typeOfWorkObj.ground_floor || "None",
      upperFloorCount: typeOfWorkObj.upper_floor_count || 0,
      isExistingWork,
    };

    if (isExistingWork) {
      return {
        ...buildingData,
        existingFloorCount: typeOfWorkObj.existing_floor_count || 0,
      };
    }

    return {
      ...buildingData,
      hasAdditionalFloor: (typeOfWorkObj.additional_floor_count || 0) > 0,
    };
  }, [jobType, isAlliedWork, isBuildingWork, isExistingWork, columnDrawingString, slabsCountString, typeOfWorkObj]);

  const jobDetailsSectionData = useMemo(
    () => ({
      jobCode: jobData.job_code || "",
      jobName: jobData.job_name || "",
      siteAddress: jobData.site_address || "",
      locality: jobData.locality || "",
      jobDate: jobData.job_date || "",
      typeOfWork: typeOfWork,
    }),
    [jobData.job_code, jobData.job_name, jobData.site_address, jobData.locality, jobData.job_date, typeOfWork]
  );

  const assignedWorkList = useMemo(() => jobData.tasks || [], [jobData.tasks]);

  const fetchJobDetails = useCallback(async () => {
    if (!id) {
      console.warn("No job ID provided for fetching job details");
      return { success: false, error: "No job ID" };
    }

    try {
      setLoadStatus({ loading: true, error: null });

      const { job, error } = await getJobDetailsById(id);

      if (error) {
        setLoadStatus({ loading: false, error });
        setJobData({}); // Reset job data on error
        return { success: false, error };
      } else {
        setJobData(job || {});
        setLoadStatus({ loading: false, error: null });
        return { success: true, data: job };
      }
    } catch (err) {
      const errorMsg = "An unexpected error occurred";
      setLoadStatus({ loading: false, error: errorMsg });
      setJobData({});
      return { success: false, error: errorMsg };
    }
  }, [id, getJobDetailsById]);

  /** Updates task working status and optionally reloads job details */
  const onTaskStatusUpdate = useCallback(
    async (taskId, status, data, reloadOnSuccess = true) => {
      if (!taskId) {
        const error = "Task ID is required for status update";
        return { error };
      }

      try {
        const { success, error } = await updateTaskWorkingStatus(taskId, status, data);

        if (error) {
          if (reloadOnSuccess) toast.error(error);
          return { error };
        }

        if (success) {
          if (reloadOnSuccess) {
            toast.success(success);
            await fetchJobDetails();
          }
          return { success };
        }

        return { error: "Unexpected response format" };
      } catch (err) {
        const errorMsg = "An unexpected error occurred while updating task status";
        if (reloadOnSuccess) toast.error(errorMsg);
        return { error: errorMsg };
      }
    },
    [updateTaskWorkingStatus, fetchJobDetails]
  );

  /** Submits an assigned task and reloads job details */
  const onTaskSubmission = useCallback(
    async (taskId, data) => {
      if (!taskId) {
        const error = "Task ID is required for task submission";
        return { success: null, error };
      }

      try {
        const { error, success } = await submitAssignedTask(taskId, data);

        if (error) {
          toast.error(error);
          return { success: null, error };
        }

        if (success) {
          toast.success(success);
          await fetchJobDetails();

          return { success, error: null };
        }

        return { success: null, error: "Unexpected response format" };
      } catch (err) {
        return { success: null, error: "An unexpected error occurred while submitting task" };
      }
    },
    [submitAssignedTask, fetchJobDetails]
  );

  const calculateTimeLeft = useCallback(() => {
    if (!Array.isArray(assignedWorkList) || assignedWorkList.length === 0) {
      return "No tasks assigned";
    }

    const now = new Date();
    let allotedTime = 0;
    let totalTimeUsed = 0;
    let additionalTime = 0;

    for (const task of assignedWorkList) {
      const taskStatus = (task.status || "").toLowerCase();

      if (taskStatus !== "completed") {
        // Calculate allotted time
        if (task.alloted_time) {
          const [hours = 0, minutes = 0] = task.alloted_time.split(":").map(Number);
          allotedTime += hours * 60 + minutes;
        }

        // Calculate time already used
        if (task.time_used) {
          const [hours = 0, minutes = 0] = task.time_used.split(":").map(Number);
          totalTimeUsed += hours * 60 + minutes;
        }

        // Calculate additional time for currently playing tasks
        if (taskStatus === "play" && task.last_play) {
          try {
            const lastPlayTime = new Date(task.last_play);
            if (!isNaN(lastPlayTime.getTime())) {
              const elapsedMinutes = Math.floor((now.getTime() - lastPlayTime.getTime()) / 60000);
              additionalTime += Math.max(0, elapsedMinutes);
            }
          } catch (error) {
            console.warn("Error calculating additional time for task:", task.id, error);
          }
        }
      }
    }

    const timeLeft = allotedTime - totalTimeUsed - additionalTime;

    if (timeLeft >= 0) {
      const hours = Math.floor(timeLeft / 60);
      const minutes = timeLeft % 60;
      return `${hours.toString().padStart(2, "0")}hrs : ${minutes.toString().padStart(2, "0")}mins left`;
    } else {
      // Overtime calculation
      const overtimeMinutes = Math.abs(timeLeft);
      const hours = Math.floor(overtimeMinutes / 60);
      const minutes = overtimeMinutes % 60;
      return `-${hours.toString().padStart(2, "0")}hrs : ${minutes.toString().padStart(2, "0")}mins (Overtime)`;
    }
  }, [assignedWorkList]);

  // Update timer every second for real-time tracking
  useEffect(() => {
    const updateTimer = () => {
      setTimeLeft(calculateTimeLeft());
    };

    updateTimer(); // Initial calculation
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [calculateTimeLeft]);

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  const contextValue = useMemo(
    () => ({
      // State
      loadStatus,
      jobData,
      timeLeft,

      // Basic job info
      jobCode,
      jobName,
      commonJobDetails,

      // Structured data
      jobDetailsSectionData,
      assignedWorkList,

      // Flags
      hasWorkAssigned,

      // Actions
      onTaskStatusUpdate,
      onTaskSubmission,
    }),
    [
      loadStatus,
      jobData,
      timeLeft,
      jobCode,
      jobName,
      commonJobDetails,
      jobDetailsSectionData,
      assignedWorkList,
      hasWorkAssigned,
      onTaskStatusUpdate,
      onTaskSubmission,
    ]
  );

  return <EmployeeJobViewContext.Provider value={contextValue}>{children}</EmployeeJobViewContext.Provider>;
}

export const useEmployeeJobView = () => {
  const context = useContext(EmployeeJobViewContext);
  if (!context) {
    throw new Error("useEmployeeJobView must be used within a EmployeeJobViewProvider");
  }
  return context;
};
